import React, { useMemo, useState } from 'react';

import { dashboardConfig } from 'config/routeConfigurations/dashboardConfig/dashboardConfig';
import { sortBy } from 'lodash';
import { useQueryClient } from 'react-query';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import type { ReportList } from 'api/endPoints/getReportList';
import { useMutationDeleteReportTag } from 'api/mutations/deleteReportTag.mutation';
import { useQueryGetReportList } from 'api/queries/getReportList.query';
import { QueryResponse as Tag, useQueryGetReportsTags } from 'api/queries/getReportsTags.query';

import { ConfirmDeleteModal } from 'components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { StarIcon } from 'components/common/Icons/common/StarIcon/StarIcon';
import { MenuGroup } from 'components/common/reportingSideMenu/components/menuGroup/MenuGroup';
import { SearchResults } from 'components/common/reportingSideMenu/components/SearchResults/SearchResults';
import { useUrlReportIdSync } from 'components/common/reportingSideMenu/hooks/useUrlReportIdSync';

import { reportingConfig } from 'components/Dashboards/Reporting/reportingConfig';
import { Button } from 'components/DesignSystem/Button';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

import classes from './ReportingSideMenu.module.scss';

const MENU_GROUP_ICON_SIZE = '2.4rem';
const INVALIDATE_API_KEYS = [
    'getReportList',
    'getAllReportTags',
    'getReport',
    'getReportTemplateList',
    'getReportAccessStatus',
];

export const ReportingSideMenu = () => {
    // state
    const [searchValue, setSearchValue] = useState('');

    const [isDeleteTagModalOpen, setIsDeleteTagModalOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    useUrlReportIdSync(setSelectedItemId);

    // router
    const history = useHistory();

    // queries and mutation
    const { data: reports } = useQueryGetReportList({
        filter: {
            filters: [],
        },
    });
    const { data: reportsTags } = useQueryGetReportsTags();
    const { mutateAsync: deleteReportTagAsync, isLoading: isMutateLoading } = useMutationDeleteReportTag();
    const queryClient = useQueryClient();

    // snackbar
    const { handleSnackBar } = useCustomSnackBar();

    // analytics
    const { sendEventWithDimensions } = useEventWithDimensions();

    const sortedReports = useMemo(() => {
        if (reports === undefined || reports === null || reports.length === 0) return [];
        return sortBy(reports, [(report) => report.name.toLowerCase()]);
    }, [reports]);

    const filteredReportsBySearch = useMemo(() => {
        if (!searchValue) return sortedReports;

        const query = searchValue.toLowerCase();

        return sortedReports.filter((report) => report.name.toLowerCase().includes(query));
    }, [searchValue, sortedReports]);

    const otherReports = useMemo(() => {
        return sortedReports.filter((report) => !report.isMyFavorite && report.tags.length === 0);
    }, [sortedReports]);

    const handleItemClick = (item: ReportList) => {
        setSelectedItemId(item.id);
        onNavigateToReport(item.id.toString());
    };

    const handleDeleteTag = (tag: Tag) => {
        setTagToDelete(tag);
        setIsDeleteTagModalOpen(true);
    };

    const onHideDeleteTagModal = () => {
        setIsDeleteTagModalOpen(false);
        setTagToDelete(null);
    };

    const onDeleteTagConfirm = async () => {
        if (tagToDelete !== null) {
            await deleteReportTagAsync([tagToDelete.id], {
                onSuccess: async () => {
                    handleSnackBar('Tag removed successfully', 'success');
                    await Promise.all(INVALIDATE_API_KEYS.map((key) => queryClient.invalidateQueries(key)));
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    setIsDeleteTagModalOpen(false);
                    setTagToDelete(null);
                },
            });
        }
    };

    const onNavigateToReport = (reportId: string): void => {
        history.push(AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH, reportId));
    };

    const handleCreateReportClick = (): void => {
        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Create Flow Started',
            label: 'none',
        });
    };

    return (
        <div className={classes.reportingSideMenu}>
            <div className={classes.createReportButtonWrapper}>
                <Button
                    component={RouterLink}
                    to={AppRoutes.buildPath(
                        AppRoutes.DASHBOARD,
                        dashboardConfig.reporting.link,
                        reportingConfig.create.link,
                    )}
                    onClick={handleCreateReportClick}
                    startIcon={<MuiIcon icon={'add'} color={'var(--button-primary-fg)'} />}
                >
                    Create report
                </Button>
            </div>

            <div className={classes.searchWrapper}>
                <TextInput
                    value={searchValue}
                    onClear={() => setSearchValue('')}
                    onChange={(e) => setSearchValue(e.target.value)}
                    id={'id'}
                    placeholder={'Search...'}
                    endIcon={<Icon name={'search'} size={'1.6rem'} color={'var(--text-placeholder)'} />}
                />
            </div>

            <div className={classes.menuGroupWrapper}>
                {searchValue ? (
                    <SearchResults
                        items={filteredReportsBySearch}
                        selectedItemId={selectedItemId?.toString() ?? ''}
                        handleItemClick={handleItemClick}
                    />
                ) : (
                    <>
                        <MenuGroup
                            items={sortedReports}
                            handleItemClick={handleItemClick}
                            filterCondition={(item: ReportList) => item.isMyFavorite}
                            groupName={'Favorites'}
                            groupId={-2}
                            isDeletable={false}
                            selectedItemId={selectedItemId?.toString() ?? ''}
                            groupIcon={<StarIcon isActive={true} fontSize={MENU_GROUP_ICON_SIZE} />}
                        />

                        {reportsTags &&
                            reportsTags.length > 0 &&
                            reportsTags.map((tag) => {
                                return (
                                    <MenuGroup
                                        key={tag.id}
                                        groupName={tag.name}
                                        groupId={tag.id}
                                        items={sortedReports}
                                        selectedItemId={selectedItemId?.toString() ?? ''}
                                        handleItemClick={handleItemClick}
                                        filterCondition={(item: ReportList) =>
                                            item.tags.some((reportTag) => reportTag.id === tag.id)
                                        }
                                        isDeletable={true}
                                        onDelete={handleDeleteTag}
                                        groupIcon={
                                            <MuiIcon
                                                icon={'label'}
                                                fontSize={MENU_GROUP_ICON_SIZE}
                                                type={'outlined'}
                                                color={`var(--text-primary)`}
                                            />
                                        }
                                    />
                                );
                            })}

                        <MenuGroup
                            groupName={'Other Reports'}
                            groupId={-1}
                            items={otherReports}
                            selectedItemId={selectedItemId?.toString() ?? ''}
                            handleItemClick={handleItemClick}
                        />
                    </>
                )}
            </div>

            <ConfirmDeleteModal
                open={isDeleteTagModalOpen}
                onClose={onHideDeleteTagModal}
                action={onDeleteTagConfirm}
                isLoading={isMutateLoading}
                title={'Delete Tag'}
            >
                <div className={classes.deleteTagModalContainer}>
                    <p>
                        Are you sure you want to delete '<span className={classes.boldText}>{tagToDelete?.name}</span>'
                        Tag ?
                    </p>
                    <p>
                        The tag will be removed from any reports that are using it, and the folder will be permanently
                        removed.
                    </p>
                </div>
            </ConfirmDeleteModal>
        </div>
    );
};
